import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, App, AutoComplete, Button, Card, Checkbox, Progress, Segmented, Select, Space, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import { MailOutlined, PhoneOutlined, ReloadOutlined, ScanOutlined, SwapOutlined } from '@ant-design/icons';
import { EMAIL_LABELS, PHONE_LABELS, channelFindingKey, type ChannelCleanerApplyResult, type ChannelFinding, type ChannelFindingStatus, type ChannelKind } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { CardTitle } from '../../components/StatCard';
import { NameCell, TableSearch } from '../../components/NameCell';
import { EmptyState } from '../../components/states';
import { errorMessage } from '../../api/client';
import { useApplyChannels, useChannelCleanerResults, useRecheckChannels, useScanChannels } from '../../features/cleanup/api';
import { fmtDateTime, plural } from '../../utils/format';
import { EntityTag } from '../../components/EntityTag';
import { CountryCodesTab } from './CountryCodesTab';

interface Row extends ChannelFinding {
  key: string;
  contactId: string;
  contactName: string;
  contactEmail: string | null;
}

type Tile = 'email' | 'phone' | 'duplicate' | 'taken';

const STATUS: Record<ChannelFindingStatus, { label: string; color: string; description: string }> = {
  new: { label: 'New', color: 'processing', description: 'Not on the contact yet: it will be added to the list.' },
  duplicate: { label: 'Merge', color: 'default', description: 'Same as an email or phone the contact already has (or one found in another field): it merges into that entry, filling in its label if it has none, and the field is cleared.' },
  taken: { label: 'Another contact', color: 'warning', description: 'This address belongs to another contact, so it cannot be added here. Maybe they are duplicates.' },
};

const TILES: { key: Tile; label: string; dot: string; description: string }[] = [
  { key: 'email', label: 'Emails to move', dot: 'medium', description: 'Email addresses the contact does not have in Emails yet.' },
  { key: 'phone', label: 'Phones to move', dot: 'medium', description: 'Phone numbers the contact does not have in Phones yet.' },
  { key: 'duplicate', label: 'Same as saved (merge)', dot: 'low', description: STATUS.duplicate.description },
  { key: 'taken', label: 'Used by another contact', dot: 'high', description: STATUS.taken.description },
];

const tileOf = (r: Row): Tile => (r.status === 'new' ? r.kind : r.status);

/** The field's value with the found address or number marked. */
function Highlight({ text, value }: { text: string; value: string }) {
  const i = text.toLowerCase().indexOf(value.toLowerCase());
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark style={{ background: '#FEF3C7', padding: 0 }}>{text.slice(i, i + value.length)}</mark>
      {text.slice(i + value.length)}
    </>
  );
}

const labelOptions = (kind: ChannelKind) => (kind === 'email' ? EMAIL_LABELS : PHONE_LABELS).map((l) => ({ value: l }));

type CleanerTab = 'move' | 'country-codes';

/** Two tabs: moving values found in other fields, and putting the country code on the Phones lists (`?tab=country-codes`). */
export function ChannelCleanerPage() {
  const [params, setParams] = useSearchParams();
  const tab: CleanerTab = params.get('tab') === 'country-codes' ? 'country-codes' : 'move';
  const tabs = (
    <Tabs
      activeKey={tab}
      onChange={(k) => setParams(k === 'move' ? {} : { tab: k }, { replace: true })}
      items={[
        { key: 'move', label: 'Move into Emails & Phones' },
        { key: 'country-codes', label: 'Country codes' },
      ]}
    />
  );
  return tab === 'country-codes' ? <CountryCodesTab tabs={tabs} /> : <MoveValuesTab tabs={tabs} />;
}

function MoveValuesTab({ tabs }: { tabs: ReactNode }) {
  const { message, modal } = App.useApp();
  const stored = useChannelCleanerResults();
  const scan = useScanChannels();
  const recheck = useRecheckChannels();
  const apply = useApplyChannels();
  const [progress, setProgress] = useState<{ scanned: number; total: number; found: number } | null>(null);
  const [applied, setApplied] = useState<{ done: number; total: number } | null>(null);
  const [outcome, setOutcome] = useState<ChannelCleanerApplyResult | null>(null);
  const [tile, setTile] = useState<Tile | 'all'>('all');
  const [kind, setKind] = useState<ChannelKind | 'all'>('all');
  const [field, setField] = useState<string | undefined>();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [removeFromSource, setRemove] = useState(true);

  const data = stored.data;

  const allRows = useMemo<Row[]>(
    () =>
      (data?.results ?? []).flatMap((c) =>
        c.findings.map((f) => ({ ...f, key: channelFindingKey(c.id, f), contactId: c.id, contactName: c.name, contactEmail: c.email })),
      ),
    [data],
  );

  const counts = useMemo(() => {
    const m: Record<Tile, number> = { email: 0, phone: 0, duplicate: 0, taken: 0 };
    for (const r of allRows) m[tileOf(r)]++;
    return m;
  }, [allRows]);

  const fieldOptions = useMemo(() => {
    const m = new Map<string, { label: string; n: number }>();
    for (const r of allRows) m.set(r.field, { label: r.fieldLabel, n: (m.get(r.field)?.n ?? 0) + 1 });
    return [...m].map(([value, { label, n }]) => ({ value, label: `${label} (${n.toLocaleString()})` }));
  }, [allRows]);

  const rows = useMemo(() => {
    const needle = q.toLocaleLowerCase();
    return allRows
      .filter((r) => tile === 'all' || tileOf(r) === tile)
      .filter((r) => kind === 'all' || r.kind === kind)
      .filter((r) => !field || r.field === field)
      .filter((r) => !needle || r.contactName.toLocaleLowerCase().includes(needle) || r.value.toLowerCase().includes(needle) || r.source.toLocaleLowerCase().includes(needle));
  }, [allRows, tile, kind, field, q]);

  const runScan = async () => {
    setSelected([]);
    setLabels({});
    setOutcome(null);
    setProgress({ scanned: 0, total: 0, found: 0 });
    try {
      stored.set(await scan.mutateAsync({ onProgress: setProgress }));
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setProgress(null);
    }
  };

  const movable = (r: Row) => r.status !== 'taken';

  const runMove = (target: Row[]) => {
    const items = target.filter(movable);
    if (!items.length || !data) return;
    const adds = items.filter((r) => r.status === 'new').length;
    modal.confirm({
      title: `Move ${plural(items.length, 'value')}?`,
      content: (
        <div>
          {adds > 0 && <p>{plural(adds, 'value')} will be added to the contacts' Emails and Phones with the labels shown.</p>}
          {items.length > adds && <p>{plural(items.length - adds, 'value')} the contact already has will merge into the existing entry.</p>}
          {removeFromSource ? (
            <p>Each value is taken out of the field it was found in. The change shows on each contact's timeline.</p>
          ) : (
            <p>The original fields are left as they are.</p>
          )}
        </div>
      ),
      okText: 'Move',
      onOk: async () => {
        setApplied({ done: 0, total: items.length });
        try {
          const result = await apply.mutateAsync({
            items: items.map((r) => ({ contactId: r.contactId, field: r.field, kind: r.kind, value: r.value, label: labels[r.key] ?? r.label })),
            removeFromSource,
            onProgress: (done) => setApplied({ done, total: items.length }),
          });
          setOutcome(result);
          message.success(`Moved ${plural(result.emails, 'email')} and ${plural(result.phones, 'phone')}${result.merged ? `, merged ${plural(result.merged, 'value')}` : ''} on ${plural(result.contacts, 'contact')}`);
          // contacts that are clean now drop out of the results
          const ids = [...new Set(items.map((r) => r.contactId))];
          const fresh = new Map((await recheck.mutateAsync(ids)).map((c) => [c.id, c]));
          stored.set({ ...data, results: data.results.flatMap((c) => (ids.includes(c.id) ? (fresh.has(c.id) ? [fresh.get(c.id)!] : []) : [c])) });
          setSelected([]);
        } catch (e) {
          message.error(errorMessage(e));
        } finally {
          setApplied(null);
        }
      },
    });
  };

  const selectedRows = rows.filter((r) => selected.includes(r.key));
  const percent = progress?.total ? Math.min(99, Math.round((progress.scanned / progress.total) * 100)) : 0;
  const movableShown = rows.filter(movable).length;

  return (
    <div className="page">
      <PageHeader
        title="Email & Phone Cleaner"
        subtitle="Find email addresses and phone numbers sitting in other fields and move them into each contact's Emails and Phones."
        extra={
          <Button type="primary" icon={data ? <ReloadOutlined /> : <ScanOutlined />} onClick={runScan} loading={scan.isPending}>
            {data ? 'Scan again' : 'Scan contacts'}
          </Button>
        }
      />
      {tabs}
      <Space orientation="vertical" size={24} style={{ width: '100%' }}>
        {progress ? (
          <Card title={<CardTitle title="Scanning" subtitle={`${progress.scanned.toLocaleString()} of ${plural(progress.total, 'contact')} checked · ${plural(progress.found, 'contact')} with values to move so far`} />}>
            <Progress percent={percent} status="active" />
          </Card>
        ) : !data ? (
          <Card title={<CardTitle title="How it works" subtitle="Nothing is changed by a scan." />}>
            <Typography.Paragraph>
              The scan reads every contact's first name, last name, job title and custom fields (text, notes, email and phone fields and so on) and picks out
              anything that looks like an email address or a phone number.
            </Typography.Paragraph>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              You then review what was found, adjust the labels (suggested from the field's name, e.g. a "Mobile number" field becomes <b>Mobile</b>, a Gmail
              address <b>Personal</b>) and move them into the contact's Emails and Phones, optionally clearing them from the field they were in.
            </Typography.Paragraph>
          </Card>
        ) : (
          <>
            <Card
              title={
                <CardTitle
                  title={allRows.length ? `${plural(allRows.length, 'value')} found on ${plural(data.results.length, 'contact')}` : 'Nothing to move'}
                  subtitle={`${plural(data.scanned, 'contact')} checked ${fmtDateTime(data.finishedAt)}. Click a box to show only those values.`}
                />
              }
            >
              {allRows.length ? (
                <div className="issue-grid">
                  {TILES.filter((t) => counts[t.key]).map((t) => {
                    const active = tile === t.key;
                    return (
                      <Tooltip key={t.key} title={t.description}>
                        <button type="button" className={`issue-tile${active ? ' issue-tile-active' : ''}`} onClick={() => setTile(active ? 'all' : t.key)} aria-pressed={active}>
                          <span className={`issue-dot issue-dot-${t.dot}`} />
                          <span className="issue-tile-label">{t.label}</span>
                          <span className="issue-tile-count">{counts[t.key].toLocaleString()}</span>
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>
              ) : (
                <Typography.Text type="secondary">No email addresses or phone numbers outside the Emails and Phones lists.</Typography.Text>
              )}
            </Card>

            {outcome && (outcome.skipped.length > 0 || outcome.merged > 0) && (
              <Alert
                type={outcome.skipped.length ? 'warning' : 'success'}
                showIcon
                closable
                onClose={() => setOutcome(null)}
                title={`Last move: ${plural(outcome.emails, 'email')} and ${plural(outcome.phones, 'phone')} added${outcome.merged ? `, ${plural(outcome.merged, 'value')} merged into existing entries` : ''}${outcome.skipped.length ? `, ${plural(outcome.skipped.length, 'value')} skipped` : ''}.`}
                description={
                  outcome.skipped.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {outcome.skipped.slice(0, 20).map((s) => (
                        <li key={`${s.contactId}${s.value}`}>
                          <EntityTag kind="contact" id={s.contactId}>{s.value}</EntityTag>: {s.reason}
                        </li>
                      ))}
                      {outcome.skipped.length > 20 && <li>and {plural(outcome.skipped.length - 20, 'more')}</li>}
                    </ul>
                  )
                }
              />
            )}

            {allRows.length > 0 && (
              <Card>
                <div className="table-toolbar">
                  <div className="toolbar-group">
                    <TableSearch value={q} onSearch={setQ} placeholder="Search name, value or field" width={260} />
                    <Segmented
                      value={kind}
                      onChange={(v) => setKind(v as ChannelKind | 'all')}
                      options={[
                        { value: 'all', label: 'All' },
                        { value: 'email', label: 'Emails' },
                        { value: 'phone', label: 'Phones' },
                      ]}
                    />
                    <Select allowClear placeholder="Found in any field" value={field} onChange={setField} options={fieldOptions} style={{ minWidth: 200 }} />
                    {tile !== 'all' && (
                      <Tag closable onClose={() => setTile('all')} color="processing">
                        {TILES.find((t) => t.key === tile)!.label}
                      </Tag>
                    )}
                  </div>
                  <div className="toolbar-group">
                    <Tooltip title="Take each value out of the field it was found in. Untick to only copy it into Emails / Phones.">
                      <Checkbox checked={removeFromSource} onChange={(e) => setRemove(e.target.checked)}>
                        Remove from original field
                      </Checkbox>
                    </Tooltip>
                    <Button
                      type="primary"
                      icon={<SwapOutlined />}
                      disabled={selected.length ? !selectedRows.some(movable) : !movableShown}
                      loading={apply.isPending}
                      onClick={() => runMove(selected.length ? selectedRows : rows)}
                    >
                      {selected.length ? `Move ${plural(selectedRows.filter(movable).length, 'selected')}` : `Move all ${movableShown.toLocaleString()} shown`}
                    </Button>
                  </div>
                </div>
                {applied && <Progress percent={Math.round((applied.done / applied.total) * 100)} status="active" style={{ marginBottom: 16 }} />}
                <Table<Row>
                  rowKey="key"
                  dataSource={rows}
                  scroll={{ x: 1200 }}
                  rowSelection={{
                    selectedRowKeys: selected,
                    onChange: (keys) => setSelected(keys as string[]),
                    preserveSelectedRowKeys: true,
                    getCheckboxProps: (r) => ({ disabled: !movable(r) }),
                  }}
                  pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (n) => plural(n, 'value') }}
                  locale={{ emptyText: <EmptyState description="No values match these filters." /> }}
                  columns={[
                    {
                      title: 'Contact',
                      key: 'contact',
                      width: 240,
                      render: (_, r) => <NameCell name={r.contactName || '(no name)'} sub={r.contactEmail ?? 'No email'} to={`/contacts/${r.contactId}`} />,
                    },
                    {
                      title: 'Found',
                      key: 'value',
                      width: 260,
                      render: (_, r) => (
                        <Space size={6}>
                          {r.kind === 'email' ? <MailOutlined style={{ color: '#6B7280' }} /> : <PhoneOutlined style={{ color: '#6B7280' }} />}
                          <Typography.Text strong copyable={{ text: r.value }}>
                            {r.value}
                          </Typography.Text>
                        </Space>
                      ),
                    },
                    {
                      title: 'In field',
                      key: 'field',
                      render: (_, r) => (
                        <div>
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {r.fieldLabel}
                          </Typography.Text>
                          <div style={{ wordBreak: 'break-word' }}>
                            <Typography.Paragraph ellipsis={{ rows: 2, tooltip: r.source }} style={{ marginBottom: 0 }}>
                              <Highlight text={r.source} value={r.value} />
                            </Typography.Paragraph>
                          </div>
                        </div>
                      ),
                    },
                    {
                      title: 'Field after',
                      key: 'after',
                      width: 170,
                      render: (_, r) =>
                        !removeFromSource ? (
                          <Typography.Text type="secondary">Unchanged</Typography.Text>
                        ) : r.remainder ? (
                          <Typography.Text ellipsis={{ tooltip: r.remainder }} style={{ maxWidth: 160 }}>
                            {r.remainder}
                          </Typography.Text>
                        ) : (
                          <Typography.Text type="secondary" italic>
                            Empty
                          </Typography.Text>
                        ),
                    },
                    {
                      title: 'Label',
                      key: 'label',
                      width: 150,
                      render: (_, r) =>
                        r.status !== 'taken' ? (
                          <AutoComplete
                            size="small"
                            value={labels[r.key] ?? r.label}
                            options={labelOptions(r.kind)}
                            onChange={(v) => setLabels((m) => ({ ...m, [r.key]: v }))}
                            style={{ width: 130 }}
                            aria-label={`Label for ${r.value}`}
                          />
                        ) : (
                          <Typography.Text type="secondary">-</Typography.Text>
                        ),
                    },
                    {
                      title: 'Status',
                      key: 'status',
                      width: 140,
                      render: (_, r) => (
                        <Tooltip title={STATUS[r.status].description}>
                          <Tag color={STATUS[r.status].color}>{STATUS[r.status].label}</Tag>
                        </Tooltip>
                      ),
                    },
                  ]}
                />
              </Card>
            )}
          </>
        )}
      </Space>
    </div>
  );
}
